// Six-section Configure schema (Required Information / General Information /
// Checkout Policies / Verifications / Evidence Vault / Integrate), with our own
// follow-up + showIf logic.
//
// Each question:
//   key      . JSON path inside the section's building_blocks bucket
//   type     . text | textarea | url | number | phone | select | chip-multi | upload
//   label    . i18n English-as-key
//   help     . optional supporting copy
//   required . counted toward the "X/Y" answered counter
//   options  . for select / chip-multi
//   allowOther. chip-multi can accept user-typed custom values
//   showIf   . conditional rendering, see utils/showIf.js for predicate shape

export const SECTION_KEYS = [
  "integrations",
  "enrichment",
  "required_information",
  "general_information",
  "checkout_policies",
  "verifications",
  "evidence_vault"
]

// Step-based labels. each section is a stop on the merchant's journey to a
// complete defense profile. Step number is for gamification ("5 of 6 done!").
export const SECTION_LABELS = {
  required_information: "Merchant identity",
  general_information: "Business story",
  checkout_policies: "Sell & protect",
  verifications: "Your defenses",
  evidence_vault: "Evidence locker",
  integrations: "Integrate",
  enrichment: "Enrichment"
}

// Step descriptors used by LeftRail to render "Step 1 / 6" badges and the
// right-pane sub-header.
export const SECTION_STEPS = Object.fromEntries(
  SECTION_KEYS.map((k, i) => [k, { stepNumber: i + 1, totalSteps: SECTION_KEYS.length }])
)

// Per-step question grouping for the wizard UI. Each step's questions render under
// numbered category headers (see SectionForm). Every group lists the question keys
// it holds, in display order. A group whose questions are ALL hidden by showIf is
// skipped, and the numbering stays sequential (so a one-time merchant never sees an
// empty "Subscription" header). Any visible question not listed here falls back to
// an ungrouped block at the end, so nothing can silently disappear.
export const SECTION_GROUPS = {
  required_information: [
    {
      title: "Business",
      description: "The basics we reference in every dispute response.",
      keys: ["business_name", "legal_name", "website_url", "industry", "industry_other"]
    },
    {
      title: "How you sell",
      description: "What you sell, and how customers pay and receive it.",
      keys: ["sales_channels", "product_types", "payment_models", "fulfillment_verification"]
    }
  ],
  general_information: [
    {
      title: "Your business",
      description: "How you describe your business to a cardholder.",
      keys: ["description", "regions"]
    },
    {
      title: "Customer support",
      description: "How customers reach you before they dispute.",
      keys: ["support_email", "support_phone"]
    },
    {
      title: "Billing & branding",
      description: "What customers see on their statement and your evidence.",
      keys: ["descriptor", "logo"]
    }
  ],
  checkout_policies: [
    {
      title: "Checkout experience",
      description: "What customers see and agree to at checkout.",
      keys: [
        "checkout_displays",
        "tos_acceptance_points",
        "renewal_consent_method",
        "post_purchase_receipts",
        "confirmation_email_timing"
      ]
    },
    {
      title: "Payment methods",
      description: "How customers pay you.",
      keys: ["express_checkout_methods"]
    },
    {
      title: "Subscription",
      description: "Consent, renewals, trials, and cancellation.",
      keys: [
        "subscription_signup_method",
        "renewal_reminder_cadence_days",
        "free_trial_offered",
        "free_trial_type",
        "free_trial_type_other",
        "free_trial_card_required",
        "free_trial_length_days",
        "cancellation_flow",
        "subscription_consent"
      ]
    },
    {
      title: "Policies",
      description: "The terms the cardholder agreed to, and where to find them.",
      keys: [
        "available_policies",
        "policy_urls.tos",
        "policy_urls.privacy",
        "policy_urls.refund",
        "policy_urls.shipping",
        "policy_urls.cancellation",
        "policy_urls.warranty",
        "policy_urls.subscription_renewal",
        "policy_urls.data_protection",
        "policy_urls.other",
        "policy_display_at_checkout"
      ]
    },
    {
      title: "Windows & guarantees",
      description: "How long customers have to get their money back.",
      keys: [
        "refund_window_days",
        "cancellation_window_days",
        "exchange_window_days",
        "money_back_guarantee_days"
      ]
    }
  ],
  verifications: [
    {
      title: "Customer identity",
      description: "Account and identity records that tie the buyer to the order.",
      keys: ["account_creation", "account_evidence_records", "signup_methods"]
    }
  ],
  evidence_vault: [
    {
      title: "Order & payment",
      description: "The confirmations and receipts customers receive.",
      keys: ["order_confirmation_screenshot", "payment_receipt_email", "refund_confirmation_email"]
    },
    {
      title: "Policies",
      description: "Your published policy documents.",
      keys: ["return_policy_document"]
    },
    {
      title: "Customer & checkout",
      description: "What the customer saw and signed up for.",
      keys: ["account_creation_email", "checkout_screen_screenshot", "tracking_page_screenshot"]
    },
    {
      title: "Subscription & usage",
      description: "Proof the customer used, or could cancel, the service.",
      keys: ["cancellation_flow_screenshot", "renewal_reminder_email"]
    }
  ]
}

// Legacy aliases. old section URLs redirect to the new ones.
export const SECTION_KEY_ALIASES = {
  business_profile: "required_information",
  business_policies: "checkout_policies",
  checkout_process: "checkout_policies",
  visual_evidence: "evidence_vault",
  tools: "evidence_vault"
}

export const SECTION_INTROS = {
  required_information:
    "Start with who you are, what you sell, and how you bill and fulfill orders. These answers shape which evidence we request for every dispute. After you click Next, we'll read your website and pre-fill the rest.",
  general_information:
    "Tell your business's story. We reference these details in dispute responses to establish who you are and how customers reach you. We pre-filled what we could from your website; review and update as needed.",
  checkout_policies:
    "Information about your checkout experience, customer communications, and policies helps us build stronger dispute responses. These details may be used to demonstrate customer authorization, terms acceptance, and awareness of your billing and refund practices.",
  verifications:
    "Information about your identity verification, authentication, and fraud prevention controls helps us build stronger responses to fraud-related disputes. These details may be used to demonstrate customer verification, transaction authorization, and fraud prevention measures that were in place at the time of purchase.",
  // Evidence has two sources: the customer-journey examples uploaded here, and the
  // transaction-specific data pulled from connected tools (the Integrate step). The
  // two-paragraph intro renders as one <p> each (SectionForm handles arrays).
  evidence_vault: [
    "Your evidence comes from two sources. First, the customer-journey examples you upload here: representative screenshots of your checkout, emails, policies, and fulfillment that show how every customer interacts with your business.",
    "Second, transaction-specific data we pull automatically from your connected tools on each dispute (set up in the Enrichment step). Together they build stronger, more complete responses."
  ],
  // Integrate step: connect the payment processors we sync disputes from.
  integrations:
    "Connect the payment processors you use to fight disputes. We sync new disputes from each connected processor and pull the transaction details we need to build your evidence, so every response starts from verified data.",
  // Enrichment step: the support / shipping / marketing tools we look the cardholder
  // up in to attach transaction-specific evidence, connected per payment processor.
  enrichment:
    "Connect the tools you run support, shipping, and marketing on. On every dispute we look up the cardholder in each connected tool and attach the relevant record (support conversation, delivery proof, email history) automatically. The more you connect for this processor, the stronger your responses."
}

const INDUSTRY_OPTIONS = [
  { value: "ecommerce", label: "Ecommerce" },
  { value: "saas", label: "SaaS" },
  { value: "subscription", label: "Subscription" },
  { value: "marketplace", label: "Marketplace" },
  { value: "digital_goods", label: "Digital goods" },
  { value: "services", label: "Services" },
  { value: "travel", label: "Travel" },
  { value: "gaming", label: "Gaming" },
  { value: "financial", label: "Financial" },
  { value: "crypto", label: "Crypto" },
  { value: "other", label: "Other" }
]

// Classification (what you sell + how you bill + how fulfilment is verified) now
// lives in the required_information step, alongside industry, so these cross-section
// gates reference required_information.* rather than general_information.*.
//
// Subscription / recurring context is driven purely by how the merchant CHARGES
// (the Payment models answer), not by their industry label or a product type.
// "Subscription" was removed as a product type (it is fully covered by the
// recurring payment model), so a one-time business never sees renewal / trial /
// cancellation questions even if it is a SaaS-by-industry merchant.
const RECURRING_SHOW_IF = { key: "required_information.payment_models", includesAny: ["recurring"] }

const RECURRING_OR_INSTALLMENTS_SHOW_IF = {
  key: "required_information.payment_models",
  includesAny: ["recurring", "installments"]
}

// ---------------------------------------------------------------------------
// Per-scenario option gating. The Step-1 menus (sales channels, fulfilment) are
// tailored to the merchant so each business type sees a relevant, rich set rather
// than one generic ecommerce list. Primary driver is the Primary industry
// (answered first); explicit product types refine it. A SaaS sees website /
// web-app channels and account / usage fulfilment, never "online store" or
// carrier tracking.
// ---------------------------------------------------------------------------

// "industry is one of N" predicate (industry is a single-select scalar, so this
// expands to an OR of equals checks). Used to gate options by industry.
const inIndustries = (list) => ({
  any: list.map((i) => ({ key: "required_information.industry", equals: i }))
})

// Industries that never ship physical goods (software / digital / money). Physical
// shipping + return / tracking questions are suppressed for these; they get the
// digital-access fulfilment menu instead, so a SaaS never sees carrier tracking.
const DIGITAL_FIRST_INDUSTRIES = ["saas", "digital_goods", "crypto", "financial"]
// Industries that deliver digitally (account / download / access / usage). Drives
// the digital fulfilment menu and the web-app channel even before product types
// are picked. (Broader than DIGITAL_FIRST: gaming + subscription deliver digitally
// but may also ship, so they are not in the physical suppressor.)
const DIGITAL_DELIVERY_INDUSTRIES = [
  "saas",
  "subscription",
  "digital_goods",
  "gaming",
  "crypto",
  "financial"
]
// Service / appointment / reservation businesses.
const SERVICE_INDUSTRIES = ["services", "travel"]

// Physical-goods context. Shows when the merchant sells physical goods OR is an
// ecommerce merchant who has not classified yet, and is never shown for the
// digital-first industries (they ship nothing). Drives the shipping / address /
// return / tracking questions throughout the flow.
const PHYSICAL_SHOW_IF = [
  {
    any: [
      { key: "required_information.product_types", includesAny: ["physical_goods"] },
      // Pre-classification default for goods industries (ecommerce + a marketplace most
      // often ship): show the physical menu until they pick product types, after which
      // the explicit answer takes over. "other" is left out so the catch-all industry is
      // purely product-driven, not physical-biased. A confirmed digital-only seller here
      // (product types set, no physical_goods) drops it.
      [
        inIndustries(["ecommerce", "marketplace"]),
        { key: "required_information.product_types", present: false }
      ]
    ]
  },
  { not: inIndustries(DIGITAL_FIRST_INDUSTRIES) }
]

// Digital / service fulfilment gates, industry-aware so a SaaS or digital business
// sees the access / login / usage menu (not shipping), and a service or travel
// business sees the completion / appointment / check-in menu, even before product
// types are picked. An explicit product type also turns the matching menu on.
const DIGITAL_SHOW_IF = {
  any: [
    { key: "required_information.product_types", includesAny: ["digital_goods"] },
    inIndustries(DIGITAL_DELIVERY_INDUSTRIES)
  ]
}
const SERVICE_SHOW_IF = {
  any: [
    { key: "required_information.product_types", includesAny: ["services"] },
    inIndustries(SERVICE_INDUSTRIES)
  ]
}
const DIGITAL_OR_SERVICE_SHOW_IF = { any: [DIGITAL_SHOW_IF, SERVICE_SHOW_IF] }

// EXPERIMENTAL step 5 (Policies and Procedures): instead of every link field showing at
// once, the merchant first ticks which policies they publish and only those link fields
// appear. Two ways a link field shows:
//   - the policy is ticked in `available_policies`
// Unticking therefore hides the field again, which is the point of the checklist. There
// used to be a third branch keeping a field visible whenever its URL already had a value;
// that made unticking look broken, and it is no longer needed now that the seeded profile
// sets `available_policies` alongside its policy_urls.
// NOTE the stored URL is kept when a policy is unticked (hidden, not cleared), so re-ticking
// restores the link the merchant already gave us.
// This is ANDed with each field's existing product / payment-model condition, so the
// conditional behaviour from the earlier steps is preserved on top of the checklist.
const POLICY_SELECTED = (slug) => ({
  key: "checkout_policies.available_policies",
  includes: slug
})

// The checklist's own options, each carrying the SAME condition as its link field, so a
// digital seller is never offered Shipping or Warranty here either. Values match the
// `policy_urls.<slug>` sub-keys exactly, which is what POLICY_SELECTED relies on.
const POLICY_OPTIONS = [
  { value: "tos", label: "Terms and Conditions" },
  { value: "privacy", label: "Privacy Policy" },
  { value: "refund", label: "Refund Policy" },
  { value: "shipping", label: "Shipping Policy", showIf: PHYSICAL_SHOW_IF },
  {
    value: "cancellation",
    label: "Cancellation Policy",
    showIf: RECURRING_OR_INSTALLMENTS_SHOW_IF
  },
  { value: "warranty", label: "Warranty Policy", showIf: PHYSICAL_SHOW_IF },
  {
    value: "subscription_renewal",
    label: "Subscription Renewal Policy",
    showIf: RECURRING_SHOW_IF
  },
  { value: "data_protection", label: "Data Protection Policy" },
  { value: "other", label: "Other policies" }
]

// Usage-dashboard evidence ("proves the cardholder actively used the service") makes
// sense for a DIGITAL / SERVICE / CRYPTO product where there is online usage to show,
// and NEVER for a pure physical seller. Crypto is included because crypto businesses
// are account/usage based (and heavily fraud-disputed). Product-type driven so it
// cannot leak via the industry label.
const SAAS_LIKE_SHOW_IF = {
  key: "required_information.product_types",
  includesAny: ["digital_goods", "services", "crypto"]
}

// "Has customer accounts" signal, read from the Step-4 "Do customers create accounts?"
// answer. This is the right gate for account-ownership evidence (sign-up verifications,
// login-history screenshot): ANY business with logins (a physical store with accounts,
// a marketplace, crypto, fintech, travel) can supply it, and an accountless / guest-
// checkout seller never sees it. Mirrors how account_evidence_records is gated.
const HAS_ACCOUNTS_SHOW_IF = { key: "verifications.account_creation", notEquals: "never" }

// Account-based businesses (where customers create logins) ask for sign-up
// verifications. Driven by what's sold and how it's charged. a digital-goods
// product (customers get an account) or a recurring payment model. NOT the
// industry label: a pure one-time physical seller has no signups to verify, so
// it never sees the sign-up verification question.
const ACCOUNT_BASED_SHOW_IF = {
  any: [
    { key: "required_information.payment_models", includesAny: ["recurring"] },
    { key: "required_information.product_types", includesAny: ["digital_goods"] }
  ]
}

// Frequency / scope scale shared by the card-present-equivalent verification
// questions (CVV, AVS, 3-D Secure). The granularity (which segments a check runs
// on, or whether it runs only on the first purchase) is captured here so the
// evidence can show the disputed charge fell inside the checks the merchant runs.
// "For the first purchase only" lets a merchant state a check runs on the first
// payment without re-stating it elsewhere (first-purchase now lives in the same
// Verifications step).
const VERIFICATION_TRIGGER_OPTIONS = [
  { value: "never", label: "Never" },
  { value: "for_first_purchase", label: "For the first purchase only" },
  { value: "for_high_risk", label: "For high-risk transactions" },
  { value: "above_amount", label: "For transactions above a certain amount" },
  { value: "selected_segments", label: "For selected cardholders, countries, or products" },
  { value: "all", label: "For all transactions" }
]

const SECTION_SCHEMA = {
  // The absolute minimum we need to read your website. We use the
  // website URL to scrape your homepage, /about, and /policies/* so we can
  // pre-fill the rest of the wizard. Three fields and you're done with this
  // step; we do the next 30 minutes of work for you.
  required_information: [
    {
      key: "business_name",
      type: "text",
      label: "Business name",
      placeholder: "Apple Inc.",
      help: "The name your customer recognizes. Pre-filled; edit if needed.",
      required: true
    },
    {
      // The registered entity, which often differs from the trading name above.
      // Issuers match the charge against the legal entity, so citing both closes
      // the "I never bought from this company" gap when the two names differ.
      key: "legal_name",
      type: "text",
      label: "Merchant Legal Name",
      placeholder: "Apple Inc.",
      help: "The registered company name, if it differs from your trading name.",
      required: true
    },
    {
      key: "website_url",
      type: "url",
      label: "Business website URL",
      placeholder: "https://website.com/",
      help: "We'll read your site after you click Next and pre-fill the rest.",
      required: true
    },
    {
      key: "industry",
      type: "select",
      label: "Primary industry",
      options: INDUSTRY_OPTIONS,
      required: true
    },
    {
      // Free-text companion for the "Other" option. Without it the answer is just
      // "other", and the industry is what shapes which evidence we ask for. Required
      // only while Other is selected; a hidden field is skipped by validation and by
      // the answered counter (utils/showIf.js -> visibleQuestions).
      key: "industry_other",
      type: "text",
      label: "Describe your industry",
      placeholder: "Online education",
      required: true,
      showIf: { key: "required_information.industry", equals: "other" }
    },
    // Sales channels (the PDF's "Where do you operate?"). How customers actually
    // buy: card-present, online, in-app, phone, or mail order. The channel changes
    // which dispute rights and evidence apply (a MO/TO or in-person sale is defended
    // differently from a card-not-present web order), so we capture it up front.
    // Distinct from the geographic "regions" field in Business story.
    {
      key: "sales_channels",
      type: "chip-multi",
      label: "Where do you sell?",
      help: "How customers buy. Each channel carries different dispute rights.",
      // Options are tailored to the industry so each business sees a relevant menu:
      // a SaaS sees website / web app / app store / direct sales (not a retail
      // storefront or carrier-tracked shipping), a retailer sees store / marketplace
      // / in-person, a service business sees booking / in-person. Website and mobile
      // app are universal. "+ Other" always covers anything missing.
      options: [
        { value: "website", label: "Website" },
        {
          value: "online_store",
          label: "Online store",
          showIf: inIndustries([
            "ecommerce",
            "subscription",
            "digital_goods",
            "gaming",
            "crypto",
            "marketplace",
            "other"
          ])
        },
        {
          value: "web_app",
          label: "Web app",
          showIf: inIndustries([
            "saas",
            "subscription",
            "digital_goods",
            "gaming",
            "crypto",
            "financial",
            "other"
          ])
        },
        { value: "mobile_app", label: "Mobile app" },
        {
          value: "app_marketplace",
          label: "App store / app marketplace",
          showIf: inIndustries([
            "saas",
            "subscription",
            "digital_goods",
            "gaming",
            "crypto",
            "marketplace"
          ])
        },
        {
          value: "online_marketplace",
          label: "Online marketplace",
          showIf: inIndustries([
            "ecommerce",
            "marketplace",
            "digital_goods",
            "gaming",
            "crypto",
            "travel",
            "services",
            "other"
          ])
        },
        {
          value: "social_commerce",
          label: "Social media / live shopping",
          showIf: inIndustries([
            "ecommerce",
            "subscription",
            "digital_goods",
            "gaming",
            "services",
            "marketplace",
            "other"
          ])
        },
        {
          value: "online_booking",
          label: "Online booking / reservations",
          showIf: inIndustries(["services", "travel", "marketplace", "other"])
        },
        {
          value: "in_person",
          label: "In person / point of sale",
          showIf: inIndustries(["ecommerce", "services", "travel", "financial", "other"])
        },
        {
          value: "moto",
          label: "Mail order / telephone order (MOTO)",
          showIf: inIndustries(["ecommerce", "services", "travel", "financial", "other"])
        },
        {
          value: "sales_team",
          label: "Sales team / direct",
          showIf: inIndustries(["saas", "services", "financial", "marketplace", "travel"])
        },
        {
          value: "reseller",
          label: "Resellers / partners",
          showIf: inIndustries(["saas", "digital_goods", "ecommerce", "financial", "gaming"])
        }
      ],
      allowOther: true,
      required: true
    },
    {
      key: "product_types",
      type: "chip-multi",
      label: "Product types",
      // "Crypto / NFT" is its own option: card networks treat crypto / non-fiat /
      // NFTs as a special transaction category with their own coding and dispute
      // handling, not ordinary digital goods. "Subscription" was removed. it is a
      // billing arrangement, already captured by the Payment models field below.
      options: [
        {
          value: "physical_goods",
          label: "Physical goods",
          tooltip:
            "Tangible items you ship to the buyer (apparel, electronics, packaged goods). Choose this if fulfilment and delivery are part of the purchase. Common for ecommerce and DTC retailers."
        },
        {
          value: "digital_goods",
          label: "Digital goods",
          tooltip:
            "Downloadable or access-based products delivered online (software, ebooks, courses, in-app items, game currency). Choose this if there is nothing to ship. Common for SaaS, app, and content sellers."
        },
        {
          value: "services",
          label: "Services",
          tooltip:
            "Work performed for the customer (consulting, design, repairs, bookings). Choose this if you bill for time or expertise rather than a product. Common for agencies and professional services."
        },
        {
          value: "crypto",
          label: "Crypto / NFT",
          tooltip:
            "Crypto, NFTs, or other non-fiat digital assets. Card networks code these as a separate, higher-risk category with their own dispute rules. Choose this only if you sell or trade these assets."
        }
      ],
      allowOther: true,
      required: true
    },
    {
      key: "payment_models",
      type: "chip-multi",
      label: "Payment models",
      options: [
        {
          value: "one_time",
          label: "One-time payment",
          tooltip:
            "A single charge for a single purchase, with no automatic future billing. Pick this if customers pay once per order. Most ecommerce and one-off services."
        },
        {
          value: "recurring",
          label: "Recurring subscription",
          tooltip:
            "Automatic repeat charges on a fixed schedule (weekly, monthly, yearly). Pick this if you run subscriptions or memberships. It drives the subscription and renewal questions later in setup."
        },
        {
          value: "installments",
          label: "Installments",
          tooltip:
            "One purchase split into several scheduled payments (pay-in-4, financing). Pick this if the total is fixed but collected over time, not an ongoing subscription."
        },
        {
          value: "ucof",
          label: "Unscheduled credential-on-file",
          tooltip:
            "Charges against a card you keep on file, with no fixed schedule (usage top-ups, ride or delivery charges, account reloads). Pick this if you bill a stored card when an event happens."
        }
      ],
      allowOther: true,
      required: true
    },
    {
      // How the merchant confirms an order reached the customer. A DECLARED
      // verification practice (not just uploaded proof) lets the evidence cite the
      // standard the merchant holds every order to, which is exactly what drives
      // an "item not received" defense. No longer physical-only: the options are
      // gated per product type (shipping for physical, login/download for digital,
      // completion/attendance for services), so every merchant declares how they
      // prove delivery. The operating model (what is sold + how it is billed + how
      // fulfilment is verified) drives evidence selection more than the industry label.
      key: "fulfillment_verification",
      type: "chip-multi",
      label: "How is fulfillment verified?",
      help: 'How you confirm delivery. Defends "not received" disputes.',
      options: [
        // Physical shipping (physical sellers / ecommerce; never shown for the
        // digital-first industries, which see the digital menu below instead).
        {
          value: "tracking_required",
          label: "Tracking number on every order",
          showIf: PHYSICAL_SHOW_IF
        },
        { value: "delivery_scan", label: "Carrier delivery scan", showIf: PHYSICAL_SHOW_IF },
        { value: "signature_required", label: "Signature on delivery", showIf: PHYSICAL_SHOW_IF },
        {
          value: "proof_of_delivery",
          label: "Photo / proof of delivery",
          showIf: PHYSICAL_SHOW_IF
        },
        { value: "auto_on_ship", label: "Marked fulfilled when shipped", showIf: PHYSICAL_SHOW_IF },
        // Digital / account-based delivery (SaaS, digital goods, gaming, crypto, fintech).
        {
          value: "account_login",
          label: "Account login or usage",
          showIf: DIGITAL_OR_SERVICE_SHOW_IF
        },
        {
          value: "access_activated",
          label: "Access or license activated",
          showIf: DIGITAL_SHOW_IF
        },
        { value: "digital_download", label: "Digital download delivered", showIf: DIGITAL_SHOW_IF },
        { value: "credentials_emailed", label: "Login details emailed", showIf: DIGITAL_SHOW_IF },
        {
          value: "usage_logged",
          label: "Product usage or activity logged",
          showIf: DIGITAL_SHOW_IF
        },
        // Service / appointment / reservation completion.
        {
          value: "service_completion",
          label: "Service completion sign-off",
          showIf: SERVICE_SHOW_IF
        },
        {
          value: "appointment_attendance",
          label: "Appointment attendance",
          showIf: SERVICE_SHOW_IF
        },
        { value: "reservation_checkin", label: "Reservation check-in", showIf: SERVICE_SHOW_IF },
        {
          value: "work_delivered",
          label: "Deliverable sent and accepted",
          showIf: SERVICE_SHOW_IF
        },
        // Customer-signed acceptance applies broadly (no gate).
        { value: "customer_acceptance", label: "Customer acceptance / signature" }
      ],
      allowOther: true,
      required: true
    }
  ],

  general_information: [
    {
      key: "description",
      type: "textarea",
      label: "Describe your business in 2-3 sentences",
      max: 500,
      required: true
    },
    {
      key: "support_email",
      type: "text",
      label: "Customer support email",
      placeholder: "support@website.com",
      required: true
    },
    {
      key: "support_phone",
      type: "phone",
      label: "Customer support phone",
      required: true
    },
    {
      key: "descriptor",
      type: "text-multi",
      label: "Billing descriptors (as they appear on the cardholder statement)",
      // Placeholder example is derived from the step-1 website URL (see QuestionField
      // domainPlaceholder), falling back to SPACEX.COM when no website was entered.
      domainPlaceholder: true,
      addLabel: "+ Add descriptor",
      help: "Pulled from your processor. Add any others you bill under.",
      required: true,
      verifiedFrom: "processors"
    },
    // Business model was removed: it overlapped the Primary industry question in
    // required_information. Evidence segmentation now derives from industry + product types +
    // payment models (evidenceDoc.deriveMerchantSegment still tolerates a legacy
    // business_model value on older saved profiles).
    {
      key: "regions",
      type: "chip-multi",
      label: "Geographic regions you operate in",
      options: [
        // selectsAll: picking Worldwide turns on every other region; clearing a
        // single region clears Worldwide. Handled in QuestionField's chip toggle.
        // The six continental regions cover the globe with no overlap (Oceania sits in
        // Asia-Pacific; Central America and the Caribbean in North America).
        { value: "worldwide", label: "Worldwide", selectsAll: true },
        { value: "north_america", label: "North America" },
        { value: "south_america", label: "South America" },
        { value: "europe", label: "Europe" },
        { value: "middle_east", label: "Middle East" },
        { value: "africa", label: "Africa" },
        { value: "asia_pacific", label: "Asia-Pacific" }
      ],
      required: false
    },
    // Year founded was removed (per the Stripe-reviewer feedback): the "established
    // merchants get more lenient weighting" note was anecdotal, and the field
    // doesn't appear in a representment letter. Monthly transaction volume + average
    // order value were removed earlier for the same reason (no win-rate value).
    {
      key: "logo",
      type: "upload",
      label: "Logo",
      help: "Square PNG or JPG, at least 400×400px. Used in the evidence PDF header. Max 10 MB.",
      accept: "image/png,image/jpeg",
      acceptHint: "PNG or JPG, 400×400px. Max 10 MB.",
      maxBytes: 10 * 1024 * 1024,
      required: false
    }
  ],

  checkout_policies: [
    // Express checkout / wallets, kept directly under "Payment methods you accept"
    // since they are the other way customers pay.
    {
      key: "express_checkout_methods",
      type: "chip-multi",
      label: "Express checkout methods enabled",
      // Apple Pay / Google Pay / PayPal are universal; Shop Pay and Amazon Pay are
      // retail-storefront wallets, so they are only offered to the retail-style
      // industries (and "+ Other" still covers any edge case).
      options: [
        { value: "apple_pay", label: "Apple Pay" },
        { value: "google_pay", label: "Google Pay" },
        {
          value: "shop_pay",
          label: "Shop Pay",
          showIf: inIndustries([
            "ecommerce",
            "marketplace",
            "subscription",
            "digital_goods",
            "other"
          ])
        },
        { value: "paypal_express", label: "PayPal Express" },
        {
          value: "amazon_pay",
          label: "Amazon Pay",
          showIf: inIndustries([
            "ecommerce",
            "marketplace",
            "subscription",
            "digital_goods",
            "other"
          ])
        }
      ],
      allowOther: true,
      required: false
    },
    //  Checkout displays + terms
    {
      key: "checkout_displays",
      type: "chip-multi",
      label: "What's shown at checkout?",
      options: [
        { value: "item_name", label: "Item name" },
        { value: "item_description", label: "Item description" },
        { value: "quantity", label: "Quantity" },
        { value: "subtotal", label: "Subtotal" },
        { value: "shipping_cost", label: "Shipping cost", showIf: PHYSICAL_SHOW_IF },
        { value: "tax", label: "Tax" },
        { value: "total", label: "Total" },
        { value: "shipping_address", label: "Shipping address", showIf: PHYSICAL_SHOW_IF },
        { value: "billing_address", label: "Billing address" },
        { value: "tos_link", label: "Terms link" },
        { value: "click_to_accept", label: "Click-to-accept terms" },
        { value: "refund_link", label: "Refund policy link" },
        { value: "subscription_terms", label: "Subscription terms", showIf: RECURRING_SHOW_IF },
        {
          value: "recurring_billing_disclosure",
          label: "Recurring billing disclosure",
          showIf: RECURRING_SHOW_IF
        },
        {
          value: "cancellation_link",
          label: "Cancellation policy",
          showIf: RECURRING_OR_INSTALLMENTS_SHOW_IF
        },
        { value: "support_contact", label: "Support contact" }
      ],
      required: false
    },
    {
      key: "tos_acceptance_points",
      type: "chip-multi",
      label: "When are terms accepted?",
      options: [
        { value: "at_signup", label: "At signup" },
        { value: "at_checkout", label: "At checkout" },
        { value: "before_payment", label: "Before payment details are entered" },
        { value: "at_renewal", label: "At renewal" },
        { value: "at_purchase", label: "At each purchase" }
      ],
      required: true
    },
    {
      key: "renewal_consent_method",
      type: "chip-multi",
      label: "How is renewal consent collected?",
      options: [
        { value: "implicit", label: "Implicit (continue using)" },
        { value: "email_reminder", label: "Reminder email (no click required)" },
        { value: "click_to_renew", label: "Click-to-renew required" }
      ],
      required: true,
      showIf: { key: "checkout_policies.tos_acceptance_points", includes: "at_renewal" }
    },
    {
      key: "post_purchase_receipts",
      type: "chip-multi",
      label: "How are receipts delivered?",
      options: [
        { value: "email", label: "Email" },
        { value: "sms", label: "SMS" },
        { value: "customer_portal", label: "Customer portal" },
        { value: "mobile_app", label: "Mobile app" },
        { value: "in_person", label: "In-person / physical receipt" }
      ],
      required: true
    },
    {
      key: "confirmation_email_timing",
      type: "select",
      label: "Order confirmation email timing",
      options: [
        { value: "instant", label: "Instant (within 30 seconds)" },
        { value: "within_5min", label: "Within 5 minutes" },
        { value: "batched_hourly", label: "Batched (hourly)" },
        { value: "batched_daily", label: "Batched (daily)" }
      ],
      required: true
    },

    //  Subscription flow follow-ups (conditional)
    {
      key: "subscription_signup_method",
      type: "select",
      variant: "cards",
      label: "Subscription signup flow",
      tooltip:
        "How a customer agrees to a recurring plan when they sign up. Single click starts the subscription in one action. Double opt-in needs a second confirmation, such as clicking an email link. Explicit consent modal shows a dedicated dialog stating the terms before they agree.",
      options: [
        { value: "single_click", label: "Single click", icon: "check" },
        { value: "double_optin", label: "Double opt-in", icon: "email" },
        { value: "explicit_consent_modal", label: "Explicit consent modal", icon: "square_lines" }
      ],
      required: false,
      showIf: RECURRING_SHOW_IF
    },
    {
      key: "renewal_reminder_cadence_days",
      type: "number",
      label: "Renewal reminder lead time (days before charge)",
      placeholder: "7",
      help: "0 means no reminder is sent.",
      required: false,
      acceptZero: true,
      // Follows the "are reminders sent" question, so it is pointless when the answer is No.
      showIf: [
        RECURRING_SHOW_IF,
        { key: "checkout_policies.renewal_reminders_automatic", notEquals: "none" }
      ]
    },

    // EXPERIMENTAL step 4, "Recurring Billing" group. These replace the standard wizard's
    // subscription_signup_method / subscription_consent / cancellation_flow for that flow
    // only: they ask about the collection channel and the cancellation channels rather than
    // the consent model, so they are separate fields instead of edits to the shared ones
    // (the standard wizard keeps its own questions untouched). "Other" is the allowOther
    // typed chip, not a dead-end option.
    {
      key: "recurring_consent_method",
      type: "chip-multi",
      label: "How is recurring consent collected?",
      options: [
        { value: "click_to_accept", label: "Click-to-accept terms & conditions at checkout" },
        { value: "signup_page", label: "Separate subscription sign-up page" },
        { value: "in_app_setup", label: "In-app, during account set-up" },
        { value: "offline_agreement", label: "Written or offline agreement" }
      ],
      allowOther: true,
      required: true,
      showIf: RECURRING_SHOW_IF
    },
    {
      key: "cancellation_channels",
      type: "chip-multi",
      label: "How can customers cancel?",
      help: "Pick every route a customer can use. More routes is stronger evidence they had a way to cancel.",
      options: [
        { value: "self_service_portal", label: "Self-service account portal" },
        { value: "email_support", label: "Email support" },
        { value: "live_chat", label: "Live chat" },
        { value: "phone", label: "Phone" },
        { value: "in_app", label: "In-app" },
        { value: "written_request", label: "Written request" }
      ],
      allowOther: true,
      required: true,
      showIf: RECURRING_SHOW_IF
    },
    {
      key: "renewal_reminders_automatic",
      type: "select",
      variant: "pills",
      label: "Are renewal reminders sent automatically?",
      options: [
        { value: "email", label: "Yes, email" },
        { value: "sms", label: "Yes, SMS" },
        { value: "email_sms", label: "Yes, email & SMS" },
        { value: "none", label: "No" },
        { value: "legal_only", label: "Only where required by law" }
      ],
      required: false,
      showIf: RECURRING_SHOW_IF
    },

    // Every policy-URL field is asked directly; the merchant fills in the links
    // they have and leaves the rest blank. Only the product-type gates remain, so
    // a SaaS is never asked for a Shipping Policy URL and a one-off seller is
    // never asked about subscription renewals. (These used to be gated on a
    // "which policies do you have?" checklist, which was removed as a redundant
    // extra step: the URL field itself already answers the question.)
    {
      // Ticking a policy here reveals its link field below.
      key: "available_policies",
      type: "chip-multi",
      label: "Which policies do you have?",
      help: "Pick the ones you publish and we will ask for a link to each.",
      options: POLICY_OPTIONS,
      required: false
    },
    {
      key: "policy_urls.tos",
      type: "url",
      label: "Terms and Conditions URL",
      placeholder: "https://website.com/terms",
      required: false,
      showIf: POLICY_SELECTED("tos")
    },
    {
      key: "policy_urls.privacy",
      type: "url",
      label: "Privacy Policy URL",
      placeholder: "https://website.com/privacy",
      required: false,
      showIf: POLICY_SELECTED("privacy")
    },
    {
      key: "policy_urls.refund",
      type: "url",
      label: "Refund Policy URL",
      placeholder: "https://website.com/refund",
      required: false,
      showIf: POLICY_SELECTED("refund")
    },
    {
      key: "policy_urls.shipping",
      type: "url",
      label: "Shipping Policy URL",
      placeholder: "https://website.com/shipping",
      required: false,
      showIf: [PHYSICAL_SHOW_IF, POLICY_SELECTED("shipping")]
    },
    {
      key: "policy_urls.cancellation",
      type: "url",
      label: "Cancellation Policy URL",
      placeholder: "https://website.com/cancellation",
      required: false,
      showIf: [RECURRING_OR_INSTALLMENTS_SHOW_IF, POLICY_SELECTED("cancellation")]
    },
    {
      key: "policy_urls.warranty",
      type: "url",
      label: "Warranty Policy URL",
      placeholder: "https://website.com/warranty",
      required: false,
      showIf: [PHYSICAL_SHOW_IF, POLICY_SELECTED("warranty")]
    },
    {
      key: "policy_urls.subscription_renewal",
      type: "url",
      label: "Subscription Renewal Policy URL",
      placeholder: "https://website.com/subscriptions",
      help: "Link to your subscription renewal terms.",
      required: false,
      showIf: [RECURRING_SHOW_IF, POLICY_SELECTED("subscription_renewal")]
    },
    {
      key: "policy_urls.data_protection",
      type: "url",
      label: "Data Protection Policy URL",
      placeholder: "https://website.com/data-protection",
      required: false,
      showIf: POLICY_SELECTED("data_protection")
    },
    {
      // Catch-all for any policy without a dedicated field above, so nothing the
      // cardholder agreed to is left uncitable. A plain URL like its siblings.
      key: "policy_urls.other",
      type: "url",
      label: "Links for your other policies",
      placeholder: "https://website.com/policies",
      help: "Any other policy page a customer agreed to.",
      required: false,
      showIf: POLICY_SELECTED("other")
    },
    {
      key: "refund_window_days",
      type: "number",
      label: "Refund window (days)",
      tooltip:
        "How many days after purchase a customer can request a refund. We cite this window to show a disputed charge fell outside the refund policy the customer agreed to.",
      placeholder: "30",
      help: "0 means refunds are not offered.",
      required: false,
      acceptZero: true
    },
    {
      key: "cancellation_window_days",
      type: "number",
      label: "Cancellation window (days)",
      tooltip:
        "How many days a customer has to cancel a subscription or order. Used to show the customer had a clear, timely way to cancel before disputing.",
      placeholder: "14",
      help: "0 means cancellation is not offered.",
      required: false,
      acceptZero: true,
      showIf: RECURRING_OR_INSTALLMENTS_SHOW_IF
    },
    {
      key: "exchange_window_days",
      type: "number",
      label: "Exchange window (days)",
      tooltip:
        "How many days a customer can exchange a physical item. Shows you offered a resolution other than a chargeback.",
      placeholder: "30",
      help: "0 means exchanges are not offered.",
      required: false,
      acceptZero: true,
      showIf: PHYSICAL_SHOW_IF
    },
    {
      key: "money_back_guarantee_days",
      type: "number",
      label: "Money-back guarantee window (days)",
      tooltip:
        "The period in which you promise a full refund, no questions asked. We use it to show the customer disputed instead of using the guarantee you offered.",
      placeholder: "14",
      help: "0 means no guarantee.",
      required: false,
      acceptZero: true
    },
    // Free trial / free plan. Trials come in several shapes (time-limited,
    // usage-limited, or a free-forever plan), so we ask the nature first and only
    // then the matching follow-up. Gated to account-based businesses (recurring or
    // digital) since a one-time physical seller has no trial to describe.
    {
      key: "free_trial_offered",
      type: "select",
      variant: "pills",
      label: "Do you offer a free trial or free plan?",
      tooltip:
        'A trial or free tier lets customers use the product before paying. We use the trial terms to show the customer knowingly continued into a paid plan, which helps defend "I did not authorize this" disputes.',
      options: [
        { value: "no", label: "No" },
        { value: "yes", label: "Yes" }
      ],
      required: true,
      showIf: ACCOUNT_BASED_SHOW_IF
    },
    {
      key: "free_trial_type",
      type: "select",
      variant: "rows",
      label: "What kind of free trial is it?",
      tooltip:
        "Trials end in different ways. Time-limited ends after a set number of days. Usage-limited ends after a set amount is used (credits, emails, API calls). A free plan stays free forever with paid features locked. This tells us how to show the customer reached the paid threshold.",
      options: [
        {
          value: "time_limited",
          label: "Time-limited",
          icon: "calendar_clock",
          description: "Ends after a set number of days."
        },
        {
          value: "usage_limited",
          label: "Usage-limited",
          icon: "analytics",
          description: "Ends after a set amount is used, like credits or emails."
        },
        {
          value: "free_plan",
          label: "Free plan",
          icon: "gift",
          description: "Free forever, with paid features locked."
        },
        {
          value: "other",
          label: "Other",
          icon: "three_dots",
          description: "A different kind of trial. Tell us how it works."
        }
      ],
      required: false,
      showIf: [
        ACCOUNT_BASED_SHOW_IF,
        { key: "checkout_policies.free_trial_offered", equals: "yes" }
      ]
    },
    {
      // Free-text companion for the "Other" option above, whose own description already
      // promises "Tell us how it works". Carries the parent conditions too, so changing
      // an earlier answer hides this even though the stored value stays 'other'.
      key: "free_trial_type_other",
      type: "text",
      label: "Describe how your trial works",
      placeholder: "Free until the first successful order",
      required: true,
      showIf: [
        ACCOUNT_BASED_SHOW_IF,
        { key: "checkout_policies.free_trial_offered", equals: "yes" },
        { key: "checkout_policies.free_trial_type", equals: "other" }
      ]
    },
    {
      key: "free_trial_card_required",
      type: "select",
      variant: "pills",
      label: "Is a payment method required to start the trial?",
      tooltip:
        "Whether you collect a card up front. A card on file is strong evidence the customer agreed to be charged when the trial converts to paid.",
      options: [
        { value: "card_required", label: "Yes, card required up front" },
        { value: "no_card", label: "No card required" }
      ],
      required: true,
      showIf: [
        ACCOUNT_BASED_SHOW_IF,
        { key: "checkout_policies.free_trial_offered", equals: "yes" }
      ]
    },
    {
      key: "free_trial_length_days",
      type: "number",
      label: "Free trial length (days)",
      placeholder: "14",
      help: "Leave at 0 for a trial with no fixed end date.",
      required: false,
      acceptZero: true,
      showIf: [
        ACCOUNT_BASED_SHOW_IF,
        { key: "checkout_policies.free_trial_offered", equals: "yes" },
        { key: "checkout_policies.free_trial_type", equals: "time_limited" }
      ]
    },
    {
      key: "cancellation_flow",
      type: "select",
      variant: "pills",
      label: "How can customers cancel?",
      options: [
        { value: "self_serve", label: "Self-serve in their account" },
        { value: "contact_support", label: "Contact support" },
        { value: "both", label: "Both" }
      ],
      help: "Self-serve cancellation improves subscription win rates.",
      required: true,
      showIf: RECURRING_SHOW_IF
    },
    {
      key: "subscription_consent",
      type: "select",
      variant: "rows",
      label: "Subscription consent model",
      tooltip:
        "How and when the customer consents to recurring charges. Implicit means continued use implies consent. Explicit at signup captures clear agreement when they subscribe. Explicit at renewal re-confirms consent before each renewal charge.",
      options: [
        // Gated to recurring billing only, so every option here is a real consent model
        // (no "not a subscription" option, which would contradict the gate).
        {
          value: "implicit",
          label: "Implicit",
          icon: "receipt_bill",
          description: "Continued use implies consent; the terms are shown at checkout."
        },
        {
          value: "explicit_at_signup",
          label: "Explicit at signup",
          icon: "check_mark",
          description: "The customer actively agrees to recurring billing when they subscribe."
        },
        {
          value: "explicit_at_renewal",
          label: "Explicit at renewal",
          icon: "arrows_rotate",
          description: "The customer re-confirms consent before each renewal charge."
        }
      ],
      required: true,
      showIf: RECURRING_SHOW_IF
    },
    {
      key: "policy_display_at_checkout",
      type: "chip-multi",
      label: "Where can customers review your policies?",
      // More places a policy is reachable = stronger "the terms were available to
      // the cardholder everywhere they looked" evidence, so we offer the full set
      // of channels (and allow a typed Other). Existing values are kept; only the
      // labels for email_confirmation / customer_portal were sharpened.
      options: [
        { value: "product_page", label: "Product page" },
        { value: "pricing_page", label: "Pricing page" },
        { value: "footer_link", label: "Footer link" },
        { value: "terms_page", label: "Terms & Conditions page" },
        { value: "checkout_page", label: "Checkout page" },
        { value: "click_to_accept", label: "Click-to-accept (checkout page)" },
        { value: "email_confirmation", label: "Order confirmation email" },
        { value: "customer_portal", label: "Customer account portal" },
        { value: "sms", label: "SMS" },
        { value: "mobile_app", label: "Mobile app" },
        { value: "physical_copy", label: "Physical copy (face-to-face sale)" }
      ],
      allowOther: true,
      required: true
    }
  ],

  verifications: [
    //  Customer accounts (a strong source of ownership evidence)
    {
      key: "account_creation",
      type: "select",
      variant: "pills",
      label: "Do customers create accounts?",
      options: [
        { value: "always", label: "Always" },
        { value: "sometimes", label: "Sometimes" },
        { value: "never", label: "Never" }
      ],
      required: true
    },
    {
      // Only relevant when accounts exist. The records a merchant can pull from an
      // account are strong evidence the genuine cardholder (not a fraudster) placed
      // the order, so we ask which ones they can provide.
      key: "account_evidence_records",
      type: "chip-multi",
      label: "Which account records can be provided as evidence?",
      options: [
        { value: "account_creation_date", label: "Account creation date" },
        { value: "login_history", label: "Login history" },
        { value: "device_information", label: "Device information" },
        { value: "ip_address", label: "IP address history" },
        { value: "order_history", label: "Purchase history" },
        { value: "password_reset_history", label: "Password reset history" },
        { value: "saved_payment_method", label: "Saved payment method history" }
      ],
      allowOther: true,
      required: true,
      showIf: HAS_ACCOUNTS_SHOW_IF
    },
    {
      key: "signup_methods",
      type: "chip-multi",
      label: "Sign-up verifications",
      tooltip:
        "Checks you run when a customer creates an account, before any purchase, like email or phone verification. This is separate from first-purchase checks, which run the first time the customer actually pays.",
      options: [
        { value: "none", label: "None", exclusive: true },
        { value: "email_verification", label: "Email verification" },
        { value: "phone_verification", label: "Phone verification" },
        { value: "gov_id", label: "Government ID" },
        { value: "biometric", label: "Biometric" },
        { value: "bank_statement", label: "Bank statement" },
        { value: "proof_of_address", label: "Proof of address" }
      ],
      required: false,
      showIf: HAS_ACCOUNTS_SHOW_IF
    }
  ],

  // Evidence vault is split visually inside SectionForm: file uploads on top,
  // integrations grid (CRM / marketing / shipping / subscriptions / analytics)
  // below. The schema here covers the upload slots; the integrations live in
  // building_blocks.tools.connected[] and are rendered by IntegrationsTab.
  evidence_vault: [
    {
      key: "order_confirmation_screenshot",
      type: "upload",
      label: "Order confirmation screenshot",
      help: "A typical post-purchase confirmation page.",
      required: false
    },
    {
      key: "payment_receipt_email",
      type: "upload",
      label: "Sample payment receipt email",
      required: false
    },
    {
      key: "refund_confirmation_email",
      type: "upload",
      label: "Sample refund confirmation email",
      required: false
    },
    // Published policy documents (screenshot or PDF). Key evidence on Consumer
    // Dispute reason codes: they show the terms the cardholder agreed to. The
    // Return policy is physical-goods only (a SaaS has nothing to return). The
    // refund policy is not uploaded here: it is cited from its URL on the
    // Policies step instead.
    {
      key: "return_policy_document",
      type: "upload",
      label: "Return policy",
      help: "A screenshot or PDF of your published return policy.",
      required: false,
      showIf: PHYSICAL_SHOW_IF
    },
    {
      key: "account_creation_email",
      type: "upload",
      label: "Sample account creation email",
      required: false,
      // Account-ownership evidence: only ask for it when the merchant actually has
      // accounts (matches account_evidence_records / signup_methods / login_history).
      showIf: HAS_ACCOUNTS_SHOW_IF
    },
    {
      key: "checkout_screen_screenshot",
      type: "upload",
      label: "Checkout screen showing terms",
      required: false
    },
    {
      key: "tracking_page_screenshot",
      type: "upload",
      label: "Tracking page screenshot (the page customers land on)",
      required: false,
      showIf: PHYSICAL_SHOW_IF
    },
    {
      key: "cancellation_flow_screenshot",
      type: "upload",
      label: "Self-serve cancellation flow screenshot",
      help: "Proves the cardholder could have cancelled.",
      required: false,
      showIf: RECURRING_SHOW_IF
    },
    {
      key: "renewal_reminder_email",
      type: "upload",
      label: "Sample renewal reminder email",
      required: false,
      showIf: RECURRING_SHOW_IF
    }
  ],
  // Integrate is a connect-your-stack step with no form questions; its
  // content is rendered by SectionForm via the IntegrationsTab/ProcessorConnect, not
  // from a schema. Kept here as an empty array so the section counters/validation
  // treat it as a valid, optional step.
  integrations: [],
  // Enrichment is likewise a connect step (support / shipping / marketing tools),
  // rendered by SectionForm via EnrichmentTab. No form questions; the connected tools
  // live per processor in byProcessor[slug].enrichment.connected[]. Empty array so the
  // counters/validation treat it as a valid, always-optional step.
  enrichment: []
}

export default SECTION_SCHEMA

// ---------------------------------------------------------------------------
// The UPDATED Configure step layout: the DEFAULT for every scenario except the
// updated flow, which is now the only one (the previous wizard has been removed).
// A re-presentation of the SAME fields into fewer, business-meaning steps. Every field
// keeps its current storage bucket, type, options, showIf, validation, website-prefill,
// and readout; only the step + group it renders under change. Because showIf uses
// absolute `section.key` paths (utils/showIf.js), a field displays and gates correctly
// under any step. Each group lists its fields as { section, key } into SECTION_SCHEMA.
// ---------------------------------------------------------------------------
// 'summary' is a real, always-visible LAST step here (the Preview & Confirm
// destination), not the finish-only route it is in the standard wizard. It carries no
// form fields, so anything iterating form fields must use getFormStepKeys() instead.
export const EXPERIMENTAL_SECTION_KEYS = [
  "integrations",
  "enrichment",
  "business_profile",
  "customer_experience",
  "policies_procedures",
  "evidence_locker",
  "summary"
]

export const EXPERIMENTAL_SECTION_LABELS = {
  integrations: "Integrate",
  enrichment: "Enrichment",
  business_profile: "Business Profile",
  customer_experience: "Customer Experience",
  policies_procedures: "Policies and Procedures",
  evidence_locker: "Evidence locker",
  summary: "Preview & Confirm"
}

// Only the steps that actually carry fields are numbered, and the total counts just
// those. Preview & Confirm is where you review what you filled in, not a step you fill,
// so it gets no number: otherwise the rail said "Step 7 of 7" while the per-processor
// switcher counted the six fillable steps ("3 of 6") and the two disagreed.
const EXPERIMENTAL_FORM_STEP_KEYS = EXPERIMENTAL_SECTION_KEYS.filter((k) => k !== "summary")
export const EXPERIMENTAL_SECTION_STEPS = Object.fromEntries(
  EXPERIMENTAL_SECTION_KEYS.map((k) => [
    k,
    {
      stepNumber: k === "summary" ? null : EXPERIMENTAL_FORM_STEP_KEYS.indexOf(k) + 1,
      totalSteps: EXPERIMENTAL_FORM_STEP_KEYS.length
    }
  ])
)

export const EXPERIMENTAL_SECTION_INTROS = {
  // Steps 1 and 2 carry their own copy here rather than reusing SECTION_INTROS, so
  // the standard wizard's Integrate / Enrichment wording stays exactly as it is.
  integrations:
    "Connect the payment processors you want Chargeback.io to manage disputes for. We automatically retrieve new disputes and the associated transaction and dispute data from each connected processor. This data is required to build and submit dispute responses.",
  enrichment:
    "Connect the platforms that support orders processed through this payment processor, such as your ecommerce platform, customer support, shipping, subscription, CRM, and marketing tools. Chargeback.io uses the transaction and dispute data from your payment processor to automatically retrieve the order, fulfillment, customer, and communication records needed to build stronger dispute responses.",
  business_profile:
    "Tell us about your business, what you sell, and how you fulfill orders. We'll use this information to tailor your dispute responses, determine which evidence is relevant, and pre-fill additional fields by analyzing your website where possible.",
  customer_experience:
    "Help us understand what your customers experience before, during, and after a purchase. These details demonstrate that customers understood what they were buying, accepted your terms, and were informed about your billing, fulfillment, subscription, and refund practices.",
  policies_procedures:
    "The terms the cardholder agreed to, where to find them, and how long they have to get their money back. Linking your published policies lets each response cite the exact terms the customer accepted.",
  // Same two-paragraph intro the standard Evidence locker step uses.
  evidence_locker: SECTION_INTROS.evidence_vault
}

// Field groups per experimental content step. `fields` are { section, key } into the
// existing SECTION_SCHEMA buckets; a group with title:null renders as a plain block.
export const EXPERIMENTAL_STEP_GROUPS = {
  business_profile: [
    {
      title: "Merchant Identity",
      description: "The basics we reference in every dispute response.",
      fields: [
        { section: "required_information", key: "business_name" },
        { section: "required_information", key: "legal_name" },
        { section: "required_information", key: "website_url" },
        { section: "general_information", key: "description" },
        { section: "general_information", key: "logo" },
        { section: "general_information", key: "descriptor" },
        { section: "required_information", key: "industry" },
        { section: "required_information", key: "industry_other" }
      ]
    },
    {
      title: "Sales Channels",
      description: "Where you sell.",
      fields: [{ section: "required_information", key: "sales_channels" }]
    },
    {
      title: "Product Types",
      description: "What you sell.",
      fields: [{ section: "required_information", key: "product_types" }]
    },
    {
      title: "Countries/Markets",
      description: "Where you operate.",
      fields: [{ section: "general_information", key: "regions" }]
    },
    {
      title: "Payments & Fulfillment",
      description: "How customers pay you and receive what they bought.",
      fields: [
        { section: "required_information", key: "payment_models" },
        { section: "required_information", key: "fulfillment_verification" }
      ]
    }
  ],
  customer_experience: [
    // How you reach the customer, and what you send them after the purchase.
    {
      title: "Support & confirmations",
      description:
        "What you send after the purchase, and how customers reach you before they dispute.",
      fields: [
        { section: "general_information", key: "support_email" },
        { section: "general_information", key: "support_phone" },
        { section: "checkout_policies", key: "post_purchase_receipts" },
        { section: "checkout_policies", key: "confirmation_email_timing" }
      ]
    },
    // The purchase moment: what was on screen and what the customer agreed to.
    {
      title: "Checkout & consent",
      description: "What the customer saw and agreed to at the moment of purchase.",
      fields: [
        { section: "checkout_policies", key: "checkout_displays" },
        { section: "checkout_policies", key: "tos_acceptance_points" },
        // 'policy_display_at_checkout' ("Where can customers review your policies?") lives on
        // the Policies and Procedures step: it describes where the policy documents are
        // reachable, not what happened at this purchase. Matches the classic grouping, which
        // files it under Policies ("and where to find them") rather than Checkout experience.
        { section: "checkout_policies", key: "renewal_consent_method" },
        { section: "checkout_policies", key: "express_checkout_methods" }
      ]
    },
    // Recurring billing: how consent is collected, how a customer gets out, and what we
    // remind them of. The standard wizard's subscription_signup_method / subscription_consent
    // / cancellation_flow are deliberately NOT listed here, so they do not render in this
    // flow (the experimental renderer draws only what a group names).
    {
      title: "Recurring Billing",
      description: "How customers agree to recurring charges, cancel, and get reminded.",
      fields: [
        { section: "checkout_policies", key: "recurring_consent_method" },
        { section: "checkout_policies", key: "cancellation_channels" },
        { section: "checkout_policies", key: "renewal_reminders_automatic" },
        { section: "checkout_policies", key: "renewal_reminder_cadence_days" }
      ]
    },
    // The free-trial questions (free_trial_offered / _type / _type_other / _card_required /
    // _length_days) are deliberately absent from this flow: the Recurring Billing group above
    // is the agreed set. They still exist in SECTION_SCHEMA and the standard wizard still asks
    // them, so listing them here again is all it takes to bring them back.
    {
      title: "Customer Identity",
      description: "Account and identity records that tie the buyer to the order.",
      fields: [
        { section: "verifications", key: "account_creation" },
        { section: "verifications", key: "account_evidence_records" },
        { section: "verifications", key: "signup_methods" }
      ]
    }
  ],
  policies_procedures: [
    // Three blocks: the policies themselves (which ones exist + a link to each), where a
    // customer can read them, then the deadlines. Titles/descriptions reuse the standard
    // wizard's own strings where they already say the right thing.
    {
      title: "Policies",
      description: "The terms the cardholder agreed to, and where to find them.",
      fields: [
        { section: "checkout_policies", key: "available_policies" },
        { section: "checkout_policies", key: "policy_urls.tos" },
        { section: "checkout_policies", key: "policy_urls.privacy" },
        { section: "checkout_policies", key: "policy_urls.refund" },
        { section: "checkout_policies", key: "policy_urls.shipping" },
        { section: "checkout_policies", key: "policy_urls.cancellation" },
        { section: "checkout_policies", key: "policy_urls.warranty" },
        { section: "checkout_policies", key: "policy_urls.subscription_renewal" },
        { section: "checkout_policies", key: "policy_urls.data_protection" },
        { section: "checkout_policies", key: "policy_urls.other" }
      ]
    },
    {
      title: "Policy visibility",
      description: "Everywhere a customer can read your policies before and after buying.",
      fields: [{ section: "checkout_policies", key: "policy_display_at_checkout" }]
    },
    {
      title: "Windows & guarantees",
      description: "How long customers have to get their money back.",
      fields: [
        { section: "checkout_policies", key: "refund_window_days" },
        { section: "checkout_policies", key: "cancellation_window_days" },
        { section: "checkout_policies", key: "exchange_window_days" },
        { section: "checkout_policies", key: "money_back_guarantee_days" }
      ]
    }
  ],
  evidence_locker: [
    {
      title: "Evidence",
      description: "Representative examples of what every customer receives and sees.",
      fields: [
        { section: "evidence_vault", key: "order_confirmation_screenshot" },
        { section: "evidence_vault", key: "payment_receipt_email" },
        { section: "evidence_vault", key: "refund_confirmation_email" },
        // 'return_policy_document' (the refund/return policy upload) is deliberately NOT
        // listed: the policy itself is captured as a link on step 5, so asking for the
        // document again here is redundant. The field definition still exists and the
        // standard wizard still asks for it.
        { section: "evidence_vault", key: "account_creation_email" },
        { section: "evidence_vault", key: "checkout_screen_screenshot" },
        { section: "evidence_vault", key: "tracking_page_screenshot" },
        { section: "evidence_vault", key: "cancellation_flow_screenshot" },
        { section: "evidence_vault", key: "renewal_reminder_email" }
      ]
    }
  ]
}

// Field def lookup within a bucket.
const findFieldDef = (section, key) => (SECTION_SCHEMA[section] || []).find((q) => q.key === key)

// The experimental steps that carry form fields (Integrate and Enrichment stay
// single, schema-less connect steps). Each aggregates fields from one or more buckets.
const EXPERIMENTAL_CONTENT_STEPS = new Set([
  "business_profile",
  "customer_experience",
  "policies_procedures",
  "evidence_locker"
])
export const isExperimentalContentStep = (stepKey) => EXPERIMENTAL_CONTENT_STEPS.has(stepKey)

// Steps whose fields are ALL optional, so Next/Finish is never blocked on them. Mirrors
// the standard wizard's evidence_vault exception (uploads are always optional).
const EXPERIMENTAL_OPTIONAL_STEPS = new Set(["evidence_locker"])
export const isOptionalStep = (stepKey) => EXPERIMENTAL_OPTIONAL_STEPS.has(stepKey)

// The bucket that owns the website URL, so the Footer knows which experimental step's
// Next should trigger the website prefill scrape.
export const EXPERIMENTAL_PREFILL_STEP = "business_profile"

// Resolve a step's groups to renderable groups, each field carrying its resolved def.
// Fields that don't resolve (a typo'd key) are dropped rather than crashing.
export const resolveStepGroups = (stepKey) =>
  (EXPERIMENTAL_STEP_GROUPS[stepKey] || []).map((g) => ({
    title: g.title,
    description: g.description,
    fields: g.fields
      .map((f) => ({ section: f.section, key: f.key, def: findFieldDef(f.section, f.key) }))
      .filter((f) => f.def)
  }))

// Flat [{ section, key, def }] for a step, in display order (used by progress +
// validation aggregation).
export const resolveStepFields = (stepKey) => resolveStepGroups(stepKey).flatMap((g) => g.fields)

// The distinct buckets a step spans (used by the Footer's reset / commit-on-leave).
export const stepSections = (stepKey) =>
  Array.from(new Set(resolveStepFields(stepKey).map((f) => f.section)))

// ---- Mode-aware accessors ----
// The updated step layout above is now the DEFAULT for every scenario. Only the
// previous wizard and its opt-out scenario have been removed, so this always returns
// the original SECTION_KEYS / SECTION_LABELS / SECTION_INTROS constants below.
// The updated 7-step flow is the only Configure flow now. The previous wizard and the
// scenario that opted back into it have both been removed, so the step list, labels and
// intros below read the EXPERIMENTAL_* tables, falling back to the originals for any key
// the newer tables do not re-map.
export const getSectionKeys = () => EXPERIMENTAL_SECTION_KEYS
// The steps that actually carry form fields: the step list minus the Preview &
// Confirm destination. Use this for navigation, counting, and completeness.
export const getFormStepKeys = () => getSectionKeys().filter((k) => k !== "summary")
// True when the flow ends on an always-visible Preview & Confirm step. That flow also
// enforces "every required field filled" before Finish and before activation.
export const hasPreviewStep = () => getSectionKeys().includes("summary")
export const getSectionLabel = (k) => EXPERIMENTAL_SECTION_LABELS[k] ?? SECTION_LABELS[k]
export const getSectionStep = (k) => EXPERIMENTAL_SECTION_STEPS[k] ?? SECTION_STEPS[k]
export const getSectionIntro = (k) => EXPERIMENTAL_SECTION_INTROS[k] ?? SECTION_INTROS[k]
// The step whose Next triggers the website prefill scrape (the one owning the website URL).
export const getPrefillStep = () => EXPERIMENTAL_PREFILL_STEP
